import { useConnector } from 'react-instantsearch-hooks-web';
import { classnames } from 'tailwindcss-classnames';
import connectStats from 'instantsearch.js/es/connectors/stats/connectStats';
import { useSearchDictionary } from './util';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { SearchDataLayerObject, gtmDatalayerPush } from 'lib/util/gtm-datalayer';

import type {
  StatsConnectorParams,
  StatsWidgetDescription,
} from 'instantsearch.js/es/connectors/stats/connectStats';
import RichTextA11yWrapper from '../RichTextA11yWrapper';
import { Field } from '@sitecore-jss/sitecore-jss-react';
import { useEffect } from 'react';

export type UseStatsProps = StatsConnectorParams;

function useStats(props?: UseStatsProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useConnector<StatsConnectorParams, StatsWidgetDescription>(connectStats as any, props);
}
interface IMyProps {
  isRitn?: boolean;
  noResultsText: Field<string>;
}
function SearchStatsWithSearch({ isRitn, noResultsText }: IMyProps, props: UseStatsProps) {
  const { nbHits, query, processingTimeMS } = useStats(props);
  const dict = useSearchDictionary();
  const resultsCount = query
    ? dict.resultsCount.replace('@count', nbHits.toString()).replace('@term', query)
    : dict.resultsCountNoQuery.replace('@count', nbHits.toString());
  const noResults = nbHits === 0 ? true : false;

  // If processingTimeMS is 0 then this is the initial component mount, not a search result response
  const serializedDataLayer = JSON.stringify(
    processingTimeMS > 0 && query
      ? { event: 'e_viewSearchResults', searchTerm: query ?? '', searchResults: nbHits.toString() }
      : null
  );

  // Wrapping in useEffect to cut down drastically on repeated pushes
  useEffect(() => {
    const gtmData: SearchDataLayerObject | null = JSON.parse(serializedDataLayer);
    if (gtmData !== null) {
      gtmDatalayerPush(gtmData as SearchDataLayerObject);
    }
  }, [serializedDataLayer]);

  if (processingTimeMS === 0) {
    return <></>;
  }

  return (
    <div className={classnames('flex-col')}>
      <div
        className={classnames(
          { 'text-blue-navy': !isRitn },
          { 'text-black': isRitn },
          'text-body',
          'font-semibold'
        )}
      >
        {resultsCount}
      </div>
      {noResults && isRitn ? (
        <div>
          <RichTextA11yWrapper
            className={classnames(
              { 'text-blue-navy': !isRitn },
              { 'text-black': isRitn },
              'mb-2',
              'mt-4'
            )}
            field={noResultsText}
          />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

const SearchStats = ({ isRitn, noResultsText }: IMyProps, props: UseStatsProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const pageEditing = sitecoreContext.pageEditing === true;
  if (pageEditing) {
    return <></>;
  }
  return <SearchStatsWithSearch isRitn={isRitn} noResultsText={noResultsText} {...props} />;
};

export default SearchStats;
